import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import Region from '../Region';
import UIAssetKeys from '../UIAssetKeys';
import gameData, { AREAS, getFoundAreasInRegion, getFoundShapesInArea, getFoundShapesInRegion, getTotalFoundShapes, REGIONS } from '../gameData';
import FontKeys from '../FontKeys';
import BeachballArea from './BeachballArea';
import SandcastleArea from './SandcastleArea';
import LifeguardStandArea from './LifeguardStandArea';
import SeagullArea from './SeagullArea';
import Game from '../scenes/Game';
import SoundAssetKeys from '../SoundAssetKeys';

export default class BeachRegion extends Region
{
    /**
     * Create the Beach region screen
     * @param {Game} scene the owner scene
     */
    constructor(scene)
    {
        super(scene, REGIONS.BEACH);

        this.isLocked = false;

        this.background = ABCya.Images.addImage(0, 0, UIAssetKeys._03_Beach_Bch_BG, this).setOrigin(0);

        this._addGraphics();
        this._createMenuButton(UIAssetKeys._Buttons_Beach_Btn_Bch_Menu3, UIAssetKeys._Buttons_Beach_Btn_Bch_Menu2, UIAssetKeys._Buttons_Beach_Btn_Bch_Menu1);
        this._createProgressButton(UIAssetKeys._Buttons_Btn_Prgrs3, UIAssetKeys._Buttons_Btn_Prgrs2, UIAssetKeys._Buttons_Btn_Prgrs1);
        this._createInformation(UIAssetKeys._03_Beach_HldingShp_Beach, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0xffffff, 0x2c2c89, 0x5f5fd6);
        this._createInstructionsPopup();
        this._createBackButton(UIAssetKeys._Buttons_Beach_Btn_Bch_Menu3, UIAssetKeys._Buttons_Beach_Btn_Bch_Back2, UIAssetKeys._Buttons_Beach_Btn_Bch_Back1);

        this.beachballArea = new BeachballArea(this);
        this.sandcastleArea = new SandcastleArea(this);
        this.lifeguardArea = new LifeguardStandArea(this);
        this.seagullArea = new SeagullArea(this);

        this._createEventListeners();

        this.lifeguardProgress = this._createAreaProgressCounter(484, 93, UIAssetKeys._03_Beach_HldingShp_Beach, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[REGIONS.BEACH].LIFEGUARD_STAND);
        this.seagullProgress = this._createAreaProgressCounter(636, 458, UIAssetKeys._03_Beach_HldingShp_Beach, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[REGIONS.BEACH].SEAGULL);
        this.beachballProgress = this._createAreaProgressCounter(786, 251, UIAssetKeys._03_Beach_HldingShp_Beach, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[REGIONS.BEACH].BEACHBALL);
        this.sandCastleProgress = this._createAreaProgressCounter(463, 538, UIAssetKeys._03_Beach_HldingShp_Beach, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[REGIONS.BEACH].SANDCASTLE);

        this._createGreatWorkPopup();
        this._createRegionUnlockedPopup();

        this.jingleAudio = ABCya.Audio.createSound(this.scene);

        //this._addReference("BeachRegionZoomedOut", "assets/reference_images/6 Get In Shape_Beach_2-01.jpg");
        //this._addReference("BeachRegionZoomedOut-AreaCounters", "assets/reference_images/8 Get In Shape_Progress1-01.jpg");
        //this._addReference("BeachRegionZoomedIn-LifeguardStandWithInstructions", "assets/reference_images/7 Get In Shape_Beach_Zoom2.jpg");
        //this._addReference("BeachRegionZoomedIn-LifeguardStand", "assets/reference_images/7 Get In Shape_Beach_Zoom3-01.jpg");
        //this._addReference("BeachRegionZoomedIn-Beachball", "assets/reference_images/7 Get In Shape_Beach_Zoom4-01.jpg");
        //this._addReference("BeachRegionZoomedIn-Sandcastle", "assets/reference_images/7 Get In Shape_Beach_Zoom5-01.jpg");
        //this._addReference("BeachRegionZoomedIn-Seagull", "assets/reference_images/7 Get In Shape_Beach_Zoom6-01.jpg");
        //ABCya.Debug.enableImagePlacerDebug(this.scene, this.background);

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        super.reset();

        this.animateFlags = false;

        this.beachball.disableInteractive();
        this.sandcastle.disableInteractive();
        this.lifeguardStand.disableInteractive();
        this.seagullBody.disableInteractive();

        this.waves.clearMask();

        this.beachballArea.reset();
        this.sandcastleArea.reset();
        this.lifeguardArea.reset();
        this.seagullArea.reset();
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        super.prepare();

        this.animateFlags = true;

        this.beachball.disableInteractive();
        this.sandcastle.disableInteractive();
        this.lifeguardStand.disableInteractive();
        this.seagullBody.disableInteractive();

        this.waves.clearMask();

        this.lifeguardProgress.setAlpha(0);
        this.beachballProgress.setAlpha(0);
        this.sandCastleProgress.setAlpha(0);
        this.seagullProgress.setAlpha(0);

        this.updateRegionData();

        this.beachballArea.prepare();
        this.sandcastleArea.prepare();
        this.lifeguardArea.prepare();
        this.seagullArea.prepare();
    }

    /**
     * Animates the states in.
     */
    async begin()
    {
        ABCya.Audio.playOneShot(SoundAssetKeys._beach_drop_in, this.scene, 1, false, 0.5);

        let tweens = [
            { targets: this.background, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 0 },
            { targets: this.ground, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.8], props: { y: this.ground.onScreenPosition.y }, offset: 0 },
            { targets: this.waves, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.8], props: { y: this.waves.onScreenPosition.y }, offset: 0 },
            { targets: this.towel, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.towel.onScreenPosition.y }, offset: 300 },
            { targets: this.grass1, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.grass1.onScreenPosition.y }, offset: 325 },
            { targets: this.grass2, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.grass2.onScreenPosition.y }, offset: 350 },
            { targets: this.grass3, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.grass3.onScreenPosition.y }, offset: 375 },
            { targets: this.grass4, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.grass4.onScreenPosition.y }, offset: 400 },
            { targets: this.grass5, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.grass5.onScreenPosition.y }, offset: 425 },
            { targets: this.grass6, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.grass6.onScreenPosition.y }, offset: 450 },
            { targets: this.grass7, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.grass7.onScreenPosition.y }, offset: 475 },
            { targets: this.grass8, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.grass8.onScreenPosition.y }, offset: 500 },
            { targets: this.grass9, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.grass9.onScreenPosition.y }, offset: 525 },
            { targets: this.palmTrunk, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.palmTrunk.onScreenPosition.y }, offset: 500 },
            { targets: this.palmLeaves, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.palmLeaves.onScreenPosition.y }, offset: 500 },
            { targets: this.beachball, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.beachball.onScreenPosition.y }, offset: 700 },
            { targets: this.chair, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.chair.onScreenPosition.y }, offset: 600 },
            { targets: this.sandcastle, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.sandcastle.onScreenPosition.y }, offset: 800 },
            { targets: this.lifeguardContainer, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.lifeguardContainer.onScreenPosition.y }, offset: 900 },
            { targets: this.seagullContainer, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.seagullContainer.onScreenPosition.y }, offset: 600 },
            { targets: this.shadows, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 1400 },
            { targets: this.informationContainer, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { x: this.informationContainer.onScreenPosition.x }, offset: 1400 },
            { targets: this.menuButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 1500 },
            { targets: this.progressButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 1600 },
        ];

        if (getFoundAreasInRegion(this.region) == 0)
        {
            tweens.push({ targets: this.regionInstructionsPopup, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { y: this.regionInstructionsPopup.onScreenPosition.y }, offset: 1700 });
            tweens.push({ targets: this.tutorialArrow, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 1600 });
        }

        this._createAreaProgressFadeIn(tweens, 1700);

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.menuButton.setInteractive();
                this.progressButton.setInteractive();

                // only enable the other areas if no areas have been found yet
                if (getFoundAreasInRegion(this.region) > 0)
                {
                    this.sandcastle.setInteractive();
                    this.lifeguardStand.setInteractive();
                    this.seagullBody.setInteractive();
                }
                this.beachball.setInteractive();

                this.waves.mask = this.waveMask;
                this._createIdleAnimations();

                if (getFoundAreasInRegion(this.region) == 0)
                {
                    ABCya.Audio.playOneShot(SoundAssetKeys._click_on_different_parts_of_screen, this.scene);
                }

                ABCya.Audio.playSound(SoundAssetKeys._beach_jingle, this.jingleAudio, 1, true);
            }
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Animates the state out.
     */
    async leave()
    {
        this.menuButton.disableInteractive();
        this.progressButton.disableInteractive();
        this.beachball.disableInteractive();
        this.sandcastle.disableInteractive();
        this.lifeguardStand.disableInteractive();
        this.seagullBody.disableInteractive();

        this._stopIdleAnimations();
        this.waves.clearMask();

        ABCya.Audio.playOneShot(SoundAssetKeys._beach_jump_up_, this.scene, 1, false, 1);

        let tweens = [
            { targets: this.jingleAudio, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { volume: 0 }, offset: 0, onComplete: () => this.jingleAudio.stop() },
            { targets: this.background, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 1700 },
            { targets: this.ground, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.8], props: { y: this.ground.offScreenPosition.y }, offset: 1700 },
            { targets: this.waves, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.8], props: { y: this.waves.offScreenPosition.y }, offset: 1700 },
            { targets: this.towel, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.towel.offScreenPosition.y }, offset: 1400 },
            { targets: this.grass1, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.grass1.offScreenPosition.y }, offset: 1375 },
            { targets: this.grass2, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.grass2.offScreenPosition.y }, offset: 1350 },
            { targets: this.grass3, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.grass3.offScreenPosition.y }, offset: 1325 },
            { targets: this.grass4, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.grass4.offScreenPosition.y }, offset: 1300 },
            { targets: this.grass5, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.grass5.offScreenPosition.y }, offset: 1275 },
            { targets: this.grass6, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.grass6.offScreenPosition.y }, offset: 1250 },
            { targets: this.grass7, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.grass7.offScreenPosition.y }, offset: 1225 },
            { targets: this.grass8, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.grass8.offScreenPosition.y }, offset: 1200 },
            { targets: this.grass9, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.grass9.offScreenPosition.y }, offset: 1175 },
            { targets: this.palmTrunk, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.palmTrunk.offScreenPosition.y }, offset: 1200 },
            { targets: this.palmLeaves, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.palmLeaves.offScreenPosition.y }, offset: 1200 },
            { targets: this.beachball, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.beachball.offScreenPosition.y }, offset: 1000 },
            { targets: this.chair, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.chair.offScreenPosition.y }, offset: 1100 },
            { targets: this.sandcastle, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.sandcastle.offScreenPosition.y }, offset: 900 },
            { targets: this.lifeguardContainer, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.lifeguardContainer.offScreenPosition.y }, offset: 800 },
            { targets: this.seagullContainer, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.seagullContainer.offScreenPosition.y }, offset: 1100 },
            { targets: this.shadows, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 300 },
            { targets: this.informationContainer, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { x: this.informationContainer.offScreenPosition.x }, offset: 300 },
            { targets: this.menuButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 200 },
            { targets: this.progressButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 100 },
            { targets: this.regionInstructionsPopup, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { y: this.regionInstructionsPopup.offScreenPosition.y }, offset: 0 },
            {
                targets: [this.beachballProgress, this.lifeguardProgress, this.seagullProgress, this.sandCastleProgress],
                duration: 500,
                ease: Phaser.Math.Easing.Quadratic.In,
                props: { alpha: 0 },
                offset: 0
            },
            { targets: this.tutorialArrow, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 0 }
        ];

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Send all game objects to their start positions. Some game objects aren't sent "offscreen" but instead set to scale of 0 or alpha of 0.
     */
    _sendTargetsOffScreen()
    {
        super._sendTargetsOffScreen();

        this._sendTargetsOffScreenAll([
            this.ground, this.waves, this.towel, this.grass1, this.grass2, this.grass3, this.grass4, this.grass5, this.grass6, this.grass7, this.grass8, this.grass9,
            this.palmTrunk, this.palmLeaves, this.beachball, this.chair, this.sandcastle, this.lifeguardContainer, this.seagullContainer
        ]);

        this.shadows.forEach(shadow => shadow.setAlpha(0));

        this.regionInstructionsPopup.setPosition(this.regionInstructionsPopup.offScreenPosition.x, this.regionInstructionsPopup.offScreenPosition.y);

        this.background.setAlpha(0);
        this.areaInstructionsPopup.setPosition(this.areaInstructionsPopup.offScreenPosition.x, this.areaInstructionsPopup.offScreenPosition.y);

        this.tutorialArrow.setAlpha(0);
    }

    /**
     * Adds the graphics for the region.
     */
    _addGraphics()
    {
        this.ground = ABCya.Images.addImage(565, 442, UIAssetKeys._03_Beach_Ground_Bch_Grnd, this);
        this.waves = ABCya.Images.addImage(715, 467, UIAssetKeys._03_Beach_Ground_Bch_Wvs, this);
        this.waveMask = ABCya.Graphics.addPolygon([{ x: 744, y: 246 }, { x: 353, y: 521 }, { x: 566, y: 642 }, { x: 999, y: 391 }], 0x000000, this).setAlpha(0).createGeometryMask();
        this.towel = ABCya.Images.addImage(650, 241, UIAssetKeys._03_Beach_Ground_BchTwl, this);

        this.grass9 = ABCya.Images.addImage(600, 141, UIAssetKeys._03_Beach_Ground_Grass9, this);
        this.palmTrunk = ABCya.Images.addImage(588, 120, UIAssetKeys._03_Beach_Ground_PalmTrnk, this);
        this.grass8 = ABCya.Images.addImage(601, 160, UIAssetKeys._03_Beach_Ground_Grass8, this);
        this.palmLeaves = ABCya.Images.addImage(571, 77, UIAssetKeys._03_Beach_Ground_PalmTop, this);

        this.grass1 = ABCya.Images.addImage(180, 394, UIAssetKeys._03_Beach_Ground_Grass1, this);
        this.grass2 = ABCya.Images.addImage(181, 345, UIAssetKeys._03_Beach_Ground_Grass2, this);
        this.grass3 = ABCya.Images.addImage(220, 343, UIAssetKeys._03_Beach_Ground_Grass3, this);
        this.grass5 = ABCya.Images.addImage(640, 193, UIAssetKeys._03_Beach_Ground_Grass5, this);
        this.grass6 = ABCya.Images.addImage(532, 183, UIAssetKeys._03_Beach_Ground_Grass6, this);
        this.grass7 = ABCya.Images.addImage(343, 350, UIAssetKeys._03_Beach_Ground_Grass7, this);

        this.beachballShadow = ABCya.Images.addImage(739, 291, UIAssetKeys._03_Beach_Beachball_Bchbll_Shdw, this);
        this.beachball = ABCya.Images.addImage(729, 279, UIAssetKeys._05_Beach_Zoom_In_Beach_Ball_BchBll, this);
        this.beachball.scale = 0.15;
        this.beachball.setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 });

        this.sandcastleShadow = ABCya.Images.addImage(435, 490, UIAssetKeys._03_Beach_Sand_Castle_Casl_Shdw, this);

        this.seagullFootprints = ABCya.Images.addImage(656, 405, UIAssetKeys._03_Beach_Seagull_Seagl_Ftprnts, this);
        this.add(this.seagullContainer = this.scene.add.container());
        this.seagullLeftLeg = ABCya.Images.addImage(497, 300, UIAssetKeys._05_Beach_Zoom_In_Seagull_Seagl_Leg_1, this.seagullContainer);
        this.seagullRightLeg = ABCya.Images.addImage(421, 267, UIAssetKeys._05_Beach_Zoom_In_Seagull_Seagl_Leg_1, this.seagullContainer);
        this.seagullBody = ABCya.Images.addImage(526, 117, UIAssetKeys._05_Beach_Zoom_In_Seagull_Seagl_Bdy, this.seagullContainer);
        this.seagullBody.setInteractive({ useHandCursor: true });
        this.seagullHead = ABCya.Images.addImage(399, 116, UIAssetKeys._05_Beach_Zoom_In_Seagull_Seagl_Hd, this.seagullContainer);
        this.seagullEyes = ABCya.Images.addImage(387, 127, UIAssetKeys._05_Beach_Zoom_In_Seagull_Seagl_Eyes_1, this.seagullContainer);
        this.seagullContainer.scale = 0.12;
        this.seagullContainer.setPosition(575, 380);

        this.lifeguardStandShadow = ABCya.Images.addImage(505, 313, UIAssetKeys._03_Beach_Lifeguard_Stand_LGStnd_Shdw, this);
        this.add(this.lifeguardContainer = this.scene.add.container());
        this.lifeguardStand = ABCya.Images.addImage(432, 262, UIAssetKeys._03_Beach_Lifeguard_Stand_LGStnd, this.lifeguardContainer);
        this.lifeguardStand.setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 });
        this.flagPole = ABCya.Images.addImage(308, 155, UIAssetKeys._03_Beach_Lifeguard_Stand_LGStnd_FlagPl, this.lifeguardContainer);

        this.flag1Rope = this.scene.add.rope(271, 80, ABCya.Images.DEFAULT_ATLAS, UIAssetKeys._03_Beach_Lifeguard_Stand_LGStnd_Flag1, 20);
        this.flag2Rope = this.scene.add.rope(271, 141, ABCya.Images.DEFAULT_ATLAS, UIAssetKeys._03_Beach_Lifeguard_Stand_LGStnd_Flag2, 20);

        this.lifeguardContainer.add([this.flag1Rope, this.flag2Rope]);

        this.animateFlags = false;
        this.count = 0;
        this.scene.events.on(Phaser.Scenes.Events.UPDATE, () =>
        {
            if (this.animateFlags)
            {
                this.count += 0.1;

                let points = this.flag1Rope.points;

                for (let i = 0; i < points.length - 2; i++)
                {
                    points[i].y = Math.sin(i * 0.25 + this.count) * (points.length - i) * 0.25;
                }

                points = this.flag2Rope.points;

                for (let i = 0; i < points.length - 2; i++)
                {
                    points[i].y = Math.sin(i * 0.25 + (this.count + 1)) * (points.length - i) * 0.25;
                }

                this.flag1Rope.setDirty();
                this.flag2Rope.setDirty();
            }
        });

        this.sandcastle = ABCya.Images.addImage(412, 470, UIAssetKeys._05_Beach_Zoom_In_Sand_Castle_SndCastle, this);
        this.sandcastle.scale = 0.19;
        this.sandcastle.setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 });

        this.grass4 = ABCya.Images.addImage(288, 322, UIAssetKeys._03_Beach_Ground_Grass4, this);
        this.chairShadow = ABCya.Images.addImage(308, 412, UIAssetKeys._03_Beach_Chair_Shdw, this);
        this.chair = ABCya.Images.addImage(275, 406, UIAssetKeys._03_Beach_Chair, this);

        this.tutorialArrow = ABCya.Images.addImage(819, 342, UIAssetKeys._Tutorial_Arw_Lft, this);

        this._setAnimationPositionsAll([
            this.ground, this.waves, this.towel, this.grass1, this.grass2, this.grass3, this.grass4, this.grass5, this.grass6, this.grass7, this.grass8, this.grass9,
            this.palmTrunk, this.palmLeaves, this.beachball, this.chair, this.sandcastle, this.lifeguardContainer, this.seagullContainer, this.seagullLeftLeg, this.seagullRightLeg
        ]);

        this.shadows = [this.beachballShadow, this.chairShadow, this.sandcastleShadow, this.lifeguardStandShadow, this.seagullFootprints];
    }

    /**
     * Create the event listeners for the objects that allow the user to zoom into the individual areas.
     */
    _createEventListeners()
    {
        this.beachball.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(BeachRegionStates.BEACHBALL));
        this.sandcastle.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(BeachRegionStates.SANDCASTLE));
        this.lifeguardStand.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(BeachRegionStates.LIFEGUARD_STAND));
        this.seagullBody.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(BeachRegionStates.SEAGULL));
    }

    /**
     * Creates the instructions popup on the region screen and the area screen.
     */
    _createInstructionsPopup()
    {
        this.add(this.regionInstructionsPopup = this.scene.add.container(521, 625));
        ABCya.Images.addImage(0, 0, UIAssetKeys._03_Beach_Instr_Popup, this.regionInstructionsPopup);
        ABCya.Text.addText(2, 5, 46, 0x2c2c89, FontKeys._retrigaregular, gameData.text.mainInstructions, this.regionInstructionsPopup).setLineSpacing(10);
        this._setAnimationPositions(this.regionInstructionsPopup, { x: this.regionInstructionsPopup.x, y: 900 });

        this.add(this.areaInstructionsPopup = this.scene.add.container(288, 670));
        ABCya.Images.addImage(0, 0, UIAssetKeys._03_Beach_Instr_Popup, this.areaInstructionsPopup);
        this.areaInstructionsPopup.firstInstructions = ABCya.Text.addText(2, 5, 46, 0x2c2c89, FontKeys._retrigaregular, gameData.text.areaInstructions0, this.areaInstructionsPopup).setLineSpacing(10);
        this.areaInstructionsPopup.secondInstructions = ABCya.Text.addText(2, 5, 46, 0x2c2c89, FontKeys._retrigaregular, gameData.text.areaInstructions1, this.areaInstructionsPopup).setLineSpacing(10);
        this._setAnimationPositions(this.areaInstructionsPopup, { x: this.areaInstructionsPopup.x, y: 900 });
        this.areaInstructionsPopup.state = -1;
        this.areaInstructionsPopup.firstInstructionsAudio = ABCya.Audio.createSound(this.scene);
    }

    /**
     * Creates the idle animations. If the idle animations are already playing, it stops it, resets everything, and starts it over.
     */
    _createIdleAnimations()
    {
        if (this.idleAnimations != null && this.idleAnimations.isPlaying())
        {
            this._stopIdleAnimations();
        }

        this.animateFlags = true;

        let tweens = [
            { targets: this.waves, duration: 2000, ease: Phaser.Math.Easing.Quadratic.InOut, props: { x: 725, y: 473 }, offset: 0 },
            {
                targets: [this.grass1, this.grass2, this.grass3, this.grass4, this.grass5, this.grass6, this.grass7, this.grass8, this.grass9],
                duration: 2000, ease: Phaser.Math.Easing.Quadratic.InOut, props: { angle: 4 }, offset: 0
            },
            { targets: this.palmLeaves, duration: 2000, ease: Phaser.Math.Easing.Quadratic.InOut, props: { angle: 5 }, offset: 500 },
            { targets: this.beachball, duration: 2000, ease: Phaser.Math.Easing.Quadratic.InOut, props: { x: this.beachball.onScreenPosition.x + 5, angle: 5 }, offset: 750 },
            { targets: this.seagullLeftLeg, duration: 750, ease: Phaser.Math.Easing.Quadratic.InOut, props: { y: this.seagullLeftLeg.onScreenPosition.y - 15 }, offset: 250, repeatDelay: 1500 },
            { targets: this.seagullRightLeg, duration: 750, ease: Phaser.Math.Easing.Quadratic.InOut, props: { y: this.seagullRightLeg.onScreenPosition.y - 15 }, offset: 1750, repeatDelay: 1500 },
            { targets: this.seagullHead, duration: 750, ease: Phaser.Math.Easing.Quadratic.InOut, props: { angle: -15 }, offset: 350, hold: 750, repeatDelay: 750 },
            { targets: this.tutorialArrow, duration: 1000, ease: Phaser.Math.Easing.Quadratic.InOut, props: { x: this.tutorialArrow.x - 10, y: this.tutorialArrow.y - 8 }, offset: 0, repeatDelay: 250 },
        ];

        this.idleAnimations = this.scene.tweens.timeline({
            tweens: tweens,
            repeat: -1,
            yoyo: true,
        });
    }

    /**
     * Stops the idle animations and resets everything back to their default spots.
     */
    _stopIdleAnimations()
    {
        if (this.idleAnimations == null) return;

        this.idleAnimations.data.forEach((tween) => 
        {
            if (tween.hasStarted)
            {
                tween.data.forEach((data) =>
                {
                    data.target[data.key] = data.start;
                });
            }
        });
        this.idleAnimations.stop();

        this.animateFlags = false;
        this.flag1Rope.setPoints(20);
        this.flag2Rope.setPoints(20);
        this.flag1Rope.setDirty();
        this.flag2Rope.setDirty();
    }

    /**
     * Call this function to update all the game data associated with this region;
     * This can be used to make the game in sync with the actual game data (after a new game reset for example).
     * Should also be called before entering the region state.
     */
    updateRegionData()
    {
        super.updateRegionData();

        this._updateAreaProgress(this.lifeguardProgress, getFoundShapesInArea(this.region, AREAS[REGIONS.BEACH].LIFEGUARD_STAND));

        this._updateAreaProgress(this.seagullProgress, getFoundShapesInArea(this.region, AREAS[REGIONS.BEACH].SEAGULL));

        this._updateAreaProgress(this.sandCastleProgress, getFoundShapesInArea(this.region, AREAS[REGIONS.BEACH].SANDCASTLE));

        this._updateAreaProgress(this.beachballProgress, getFoundShapesInArea(this.region, AREAS[REGIONS.BEACH].BEACHBALL));
    }

    /**
     * Zooms into the desired area.
     * @param {string} area the AREAS enum value
     */
    _zoomIntoArea(area)
    {
        super._zoomIntoArea(area);

        this._currentState = area;
        this._stopIdleAnimations();

        this.menuButton.disableInteractive();
        this.progressButton.disableInteractive();

        this.beachball.disableInteractive();
        this.sandcastle.disableInteractive();
        this.lifeguardStand.disableInteractive();
        this.seagullBody.disableInteractive();

        this.areaInstructionsPopup.firstInstructions.setAlpha(1);
        this.areaInstructionsPopup.state = -1;
        this.areaInstructionsPopup.secondInstructions.setAlpha(0);

        let tweens = [];

        if (this._currentState == BeachRegionStates.BEACHBALL)
        {
            tweens.push(...this.beachballArea.zoomIn());
        }
        else if (this._currentState == BeachRegionStates.SANDCASTLE)
        {
            tweens.push(...this.sandcastleArea.zoomIn());
        }
        else if (this._currentState == BeachRegionStates.LIFEGUARD_STAND)
        {
            tweens.push(...this.lifeguardArea.zoomIn());
        }
        else if (this._currentState == BeachRegionStates.SEAGULL)
        {
            tweens.push(...this.seagullArea.zoomIn());
        }

        tweens.push(
            {
                targets: [
                    this.ground, this.waves, this.towel, this.grass1, this.grass2, this.grass3, this.grass4, this.grass5,
                    this.grass6, this.grass7, this.grass8, this.grass9, this.palmTrunk, this.palmLeaves, this.chair, this.beachballShadow,
                    this.chairShadow, this.sandcastleShadow, this.lifeguardStandShadow, this.seagullFootprints, this.beachballProgress,
                    this.lifeguardProgress, this.seagullProgress, this.sandCastleProgress, this.tutorialArrow
                ],
                duration: 500,
                props: { alpha: 0 },
                ease: Phaser.Math.Easing.Quadratic.Out,
                offset: 0
            },
            { targets: [this.menuButton, this.progressButton], duration: 500, props: { scale: 0 }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.informationContainer, duration: 500, props: { x: this.informationContainer.offScreenPosition.x }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.regionInstructionsPopup, duration: 500, props: { y: this.regionInstructionsPopup.offScreenPosition.y }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.backButton, duration: 500, props: { scale: 1 }, ease: Phaser.Math.Easing.Back.Out, offset: 500 },
        );

        if (getFoundShapesInRegion(this.region) == 0)
        {
            // show the area instructions if no shapes have been found
            tweens.push(
                { targets: this.areaInstructionsPopup, duration: 500, props: { y: this.areaInstructionsPopup.onScreenPosition.y }, ease: Phaser.Math.Easing.Back.Out, offset: 500 },
            );
            ABCya.Audio.playSound(SoundAssetKeys._select_shape_from_list, this.areaInstructionsPopup.firstInstructionsAudio, 1, false, 0.75);
            this.areaInstructionsPopup.state = 0;
        }

        this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.backButton.setInteractive();
                if (this._currentState == BeachRegionStates.BEACHBALL)
                {
                    this.beachballArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == BeachRegionStates.SANDCASTLE)
                {
                    this.sandcastleArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == BeachRegionStates.LIFEGUARD_STAND)
                {
                    this.lifeguardArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == BeachRegionStates.SEAGULL)
                {
                    this.seagullArea.setAreaShapeButtonInteractable(true);
                }
            }
        });
    }

    /**
     * Leaves the zoomed in area.
     */
    _leaveZoomedInState()
    {
        super._leaveZoomedInState();

        this.updateRegionData();

        this.backButton.disableInteractive();

        let tweens = [];

        if (this._currentState == BeachRegionStates.BEACHBALL)
        {
            tweens.push(...this.beachballArea.zoomOut());
        }
        else if (this._currentState == BeachRegionStates.SANDCASTLE)
        {
            tweens.push(...this.sandcastleArea.zoomOut());
        }
        else if (this._currentState == BeachRegionStates.LIFEGUARD_STAND)
        {
            tweens.push(...this.lifeguardArea.zoomOut());
        }
        else if (this._currentState == BeachRegionStates.SEAGULL)
        {
            tweens.push(...this.seagullArea.zoomOut());
        }

        tweens.push(
            {
                targets: [
                    this.ground, this.waves, this.towel, this.grass1, this.grass2, this.grass3, this.grass4, this.grass5,
                    this.grass6, this.grass7, this.grass8, this.grass9, this.palmTrunk, this.palmLeaves, this.chair
                ],
                duration: 500,
                props: { alpha: 1 },
                ease: Phaser.Math.Easing.Quadratic.Out,
                offset: 500
            },
            { targets: this.backButton, duration: 500, props: { scale: 0 }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: [this.menuButton, this.progressButton], duration: 500, props: { scale: 1 }, ease: Phaser.Math.Easing.Back.Out, offset: 750 },
            { targets: this.informationContainer, duration: 500, props: { x: this.informationContainer.onScreenPosition.x }, ease: Phaser.Math.Easing.Back.Out, offset: 500 },
            { targets: this.shadows, duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 1000 },
            { targets: this.areaInstructionsPopup, duration: 500, props: { y: this.areaInstructionsPopup.offScreenPosition.y }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
        );

        this._createAreaProgressFadeIn(tweens, 1000);

        this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.menuButton.setInteractive();
                this.progressButton.setInteractive();

                if (this.scene.downtownRegion.isLocked && gameData.region_unlock_requirements[REGIONS.DOWNTOWN] <= getTotalFoundShapes())
                {
                    // unlock the downtown regions
                    this.scene.downtownRegion.isLocked = false;
                    this._playUnlockAnimation();
                }
                else this._checkSpaceRegionLockStatus();

                this.beachball.setInteractive();
                this.sandcastle.setInteractive();
                this.lifeguardStand.setInteractive();
                this.seagullBody.setInteractive();

                this._createIdleAnimations();
            }
        });
    }

    /**
     * Create area progress fade in tweens for areas that have been found.
     * @param {any[]} tweens the current array of tweens
     * @param {number} offset the offset of the progress fade in tweens
     */
    _createAreaProgressFadeIn(tweens, offset)
    {
        if (gameData.saved_data[this.region][AREAS[REGIONS.BEACH].LIFEGUARD_STAND].found)
        {
            tweens.push({ targets: this.lifeguardProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[REGIONS.BEACH].BEACHBALL].found)
        {
            tweens.push({ targets: this.beachballProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[REGIONS.BEACH].SANDCASTLE].found)
        {
            tweens.push({ targets: this.sandCastleProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[REGIONS.BEACH].SEAGULL].found)
        {
            tweens.push({ targets: this.seagullProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
    }
}

/**
 * The individual area states for the Beach region.
 */
export const BeachRegionStates = {
    BEACHBALL: "Beachball",
    SEAGULL: "Seagull",
    SANDCASTLE: "Sandcastle",
    LIFEGUARD_STAND: "Lifeguard Stand"
}